import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Skeleton from '@mui/material/Skeleton';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { submitContact } from '../api';
import { useContactSettings } from '../hooks/useContactSettings';
import { MOBILITY_ICON_REGISTRY } from '../config/mobility-icon-registry';

const ACCENT = '#4fc3f7';

const FALLBACK_TYPES = [
  { key: 'reclamation', labelKey: 'contact.types.reclamation', icon: 'ReportProblem' },
  { key: 'avis', labelKey: 'contact.types.avis', icon: 'ThumbUp' },
  { key: 'demande', labelKey: 'contact.types.demande', icon: 'Help' },
];

const FALLBACK_FIELDS = [
  { key: 'name', labelKey: 'contact.name', field_type: 'text', required: true },
  { key: 'email', labelKey: 'contact.email', field_type: 'email', required: true },
  { key: 'subject', labelKey: 'contact.subject', field_type: 'text', required: true },
  { key: 'message', labelKey: 'contact.message', field_type: 'textarea', required: true },
];

// Mirrors max_length_for_field in the API (infrastructure/contact_settings.py):
// keys denormalised onto ContactMessage columns keep the column width.
const DENORMALIZED_MAX_LENGTHS = { name: 255, email: 254, subject: 255 };
const DEFAULT_TEXT_MAX_LENGTH = 255;
const DEFAULT_TEXTAREA_MAX_LENGTH = 5000;

function maxLengthForField(field) {
  if (field.key in DENORMALIZED_MAX_LENGTHS) return DENORMALIZED_MAX_LENGTHS[field.key];
  if (field.field_type === 'textarea') return DEFAULT_TEXTAREA_MAX_LENGTH;
  if (field.field_type === 'email') return DENORMALIZED_MAX_LENGTHS.email;
  return DEFAULT_TEXT_MAX_LENGTH;
}

const inputSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: 'rgba(255,255,255,0.04)',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.35)' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
  '& .MuiInputBase-input': { color: '#fff' },
};

function copy(settings, field, fallback) {
  const configuredValue = settings?.[field];
  return typeof configuredValue === 'string' && configuredValue.trim()
    ? configuredValue
    : fallback;
}

function buildInitialValues(types, fields) {
  const values = { type: types[0]?.key || 'reclamation' };
  fields.forEach((field) => {
    values[field.key] = '';
  });
  return values;
}

export default function ContactForm() {
  const { t } = useTranslation();
  const { settings, isLoading: isLoadingSettings, error: settingsError, reload } = useContactSettings();

  const types = useMemo(() => {
    const configured = Array.isArray(settings?.types)
      ? settings.types.filter((row) => row?.is_active !== false)
      : [];
    if (configured.length > 0) {
      return [...configured].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }
    return FALLBACK_TYPES.map((row) => ({
      ...row,
      label: t(row.labelKey),
    }));
  }, [settings, t]);

  const fields = useMemo(() => {
    const configured = Array.isArray(settings?.form_fields)
      ? settings.form_fields.filter((row) => row?.is_active !== false)
      : [];
    if (configured.length > 0) {
      return [...configured].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }
    return FALLBACK_FIELDS.map((row) => ({
      ...row,
      label: t(row.labelKey),
    }));
  }, [settings, t]);

  const [form, setForm] = useState(() => buildInitialValues(types, fields));
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [serverError, setServerError] = useState('');

  useEffect(() => {
    setForm((previous) => {
      // Never drop a key: values typed before the config settled must survive,
      // and only the configured fields are read back when building the payload.
      const next = { ...previous };
      fields.forEach((field) => {
        if (next[field.key] === undefined) next[field.key] = '';
      });
      if (!types.some((row) => row.key === next.type)) {
        next.type = types[0]?.key || '';
      }
      return next;
    });
  }, [types, fields]);

  function setField(fieldKey) {
    return (event) => {
      setForm((current) => ({ ...current, [fieldKey]: event.target.value }));
      setErrors((current) => ({ ...current, [fieldKey]: undefined }));
    };
  }

  function validate() {
    const nextErrors = {};
    fields.forEach((field) => {
      const value = String(form[field.key] ?? '').trim();
      if (field.required && !value) {
          nextErrors[field.key] = t('contact.errors.fieldRequired');
        return;
      }
      const maxLength = maxLengthForField(field);
      if (value.length > maxLength) {
        nextErrors[field.key] = t('contact.errors.fieldTooLong', { max: maxLength });
        return;
      }
      if (value && field.field_type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        nextErrors[field.key] = t('contact.errors.emailInvalid');
      }
    });
    return nextErrors;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    const nextErrors = validate();
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    setStatus('loading');
    setServerError('');

    const payload = { type: form.type };
    fields.forEach((field) => {
      payload[field.key] = String(form[field.key] ?? '').trim();
    });

    try {
      await submitContact(payload);
      setStatus('success');
      setForm(buildInitialValues(types, fields));
    } catch (err) {
      const fieldErrors = err?.fieldErrors || {};
      const rendered = fields.filter((field) => fieldErrors[field.key]);
      setErrors(
        rendered.reduce((acc, field) => ({ ...acc, [field.key]: fieldErrors[field.key] }), {})
      );

      // Messages about keys we do not render (e.g. `type`) would be invisible.
      const orphanMessages = Object.keys(fieldErrors)
        .filter((key) => !rendered.some((field) => field.key === key))
        .map((key) => fieldErrors[key]);
      setServerError(
        [err.message || t('contact.errors.generic'), ...orphanMessages].join(' ')
      );
      setStatus('error');
    }
  }

  // The configured field set decides what may be submitted, so no input is
  // offered until the request settles: otherwise a value typed against the
  // default form would silently vanish when the real config arrives.
  if (isLoadingSettings) {
    return (
      <Stack spacing={3}>
        <ContactHeader
          title={t('contact.pageTitle')}
          subtitle={t('contact.pageSubtitle')}
        />
        <Stack spacing={2} role="status" aria-label={t('contact.loading')}>
          <Skeleton variant="rounded" height={44} />
          <Skeleton variant="rounded" height={56} />
          <Skeleton variant="rounded" height={56} />
          <Skeleton variant="rounded" height={120} />
          <Skeleton variant="rounded" height={48} />
        </Stack>
      </Stack>
    );
  }

  // Submitting the hard-coded fallback shape would be rejected by the API,
  // which validates against the configured types and fields.
  if (settingsError) {
    return (
      <Stack spacing={3}>
        <ContactHeader
          title={t('contact.pageTitle')}
          subtitle={t('contact.pageSubtitle')}
        />
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={reload}>
              {t('contact.retry')}
            </Button>
          }
        >
          <AlertTitle>{t('contact.errors.unavailableTitle')}</AlertTitle>
          {t('contact.errors.unavailableDesc')}
        </Alert>
      </Stack>
    );
  }

  if (status === 'success') {
    return (
      <Stack spacing={3}>
        <ContactHeader
          title={copy(settings, 'page_title', t('contact.pageTitle'))}
          subtitle={copy(settings, 'page_subtitle', t('contact.pageSubtitle'))}
        />
        <Stack alignItems="center" spacing={2} sx={{ py: 4, px: 3, textAlign: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 64, color: '#66bb6a' }} />
          <Typography variant="h6" fontWeight={700} color="#fff">
            {copy(settings, 'success_title', t('contact.successTitle'))}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {copy(settings, 'success_description', t('contact.successDesc'))}
          </Typography>
          <Button variant="outlined" sx={{ mt: 2 }} onClick={() => setStatus('idle')}>
            {copy(settings, 'new_message_label', t('contact.newMessage'))}
          </Button>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      <ContactHeader
        title={copy(settings, 'page_title', t('contact.pageTitle'))}
        subtitle={copy(settings, 'page_subtitle', t('contact.pageSubtitle'))}
      />
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Stack spacing={3}>
          {types.length > 0 && (
            <Box>
              <Typography
                variant="caption"
                color="rgba(255,255,255,0.5)"
                sx={{ mb: 1, display: 'block', textTransform: 'uppercase', letterSpacing: 1 }}
              >
                {copy(settings, 'type_label', t('contact.typeLabel'))}
              </Typography>
              <ToggleButtonGroup
                exclusive
                fullWidth
                value={form.type}
                onChange={(_, value) => value && setForm((current) => ({ ...current, type: value }))}
                sx={{
                  gap: 1,
                  flexWrap: 'wrap',
                  '& .MuiToggleButtonGroup-grouped': {
                    border: '1px solid rgba(255,255,255,0.15) !important',
                    borderRadius: '10px !important',
                  },
                }}
              >
                {types.map((type) => {
                  const Icon = MOBILITY_ICON_REGISTRY[type.icon] || HelpOutlineIcon;
                  return (
                    <ToggleButton
                      key={type.key}
                      value={type.key}
                      sx={{
                        flex: '1 1 120px',
                        py: 1.2,
                        gap: 0.8,
                        color: 'rgba(255,255,255,0.5)',
                        bgcolor: 'rgba(255,255,255,0.04)',
                        textTransform: 'capitalize',
                        fontWeight: 600,
                        fontSize: '0.78rem',
                        '&.Mui-selected': {
                          color: '#fff',
                          bgcolor: `${ACCENT}22`,
                          borderColor: `${ACCENT} !important`,
                          boxShadow: `0 0 0 1px ${ACCENT}`,
                        },
                        '&.Mui-selected svg': { color: ACCENT },
                      }}
                    >
                      <Icon fontSize="small" />
                      {type.label}
                    </ToggleButton>
                  );
                })}
              </ToggleButtonGroup>
            </Box>
          )}

          {fields.map((field) => (
            <TextField
              key={field.key}
              label={field.label}
              value={form[field.key] ?? ''}
              onChange={setField(field.key)}
              error={Boolean(errors[field.key])}
              helperText={errors[field.key]}
              type={field.field_type === 'email' || field.field_type === 'tel' || field.field_type === 'number'
                ? field.field_type
                : 'text'}
              multiline={field.field_type === 'textarea'}
              minRows={field.field_type === 'textarea' ? 4 : undefined}
              required={Boolean(field.required)}
              slotProps={{ htmlInput: { maxLength: maxLengthForField(field) } }}
              fullWidth
              sx={inputSx}
            />
          ))}

          {status === 'error' && (
            <Alert severity="error">
              <AlertTitle>{t('contact.errors.submitFailed')}</AlertTitle>
              {serverError}
            </Alert>
          )}

          <Button
            type="submit"
            variant="contained"
            size="large"
            endIcon={status === 'loading' ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
            disabled={status === 'loading'}
            sx={{
              bgcolor: ACCENT,
              color: '#0a1628',
              fontWeight: 700,
              py: 1.4,
              fontSize: '0.95rem',
              '&:hover': { bgcolor: ACCENT, filter: 'brightness(1.12)' },
              '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.3)' },
            }}
          >
            {status === 'loading'
              ? copy(settings, 'sending_label', t('contact.sending'))
              : copy(settings, 'submit_label', t('contact.submit'))}
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
}

function ContactHeader({ title, subtitle }) {
  return (
    <Stack spacing={0.75} sx={{ textAlign: 'center' }}>
      <Typography component="h1" variant="h5" fontWeight={800} color="#fff">
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {subtitle}
      </Typography>
    </Stack>
  );
}
